import { AppStore } from '@apparatix/stores/AppStore';
import { useBuildStore } from '@apparatix/stores/useBuildStore';
import { AuthProvider, registerSuspectHandler, useSessionStore } from '@apparatix/stores/useSessionStore';
import { LocalStorageKeys } from '@apparatix/constants/LocalStorageKeys';
import { Milliseconds } from '@apparatix/constants/Milliseconds';
import { useBuildVersionCheck } from './useBuildVersionCheck';
import { useLocalStorage } from './useLocalStorage';

export interface SessionStatusPayload {
  authenticated: boolean;
  userId?: number;
  username?: string;
  impersonating?: boolean;
  authProvider: AuthProvider;
  csrfToken: string;
  build: string;
  contract: number;
}

const STATUS_URL = '/session/status';

/** Floor between probes, so a burst of failing requests results in one round-trip. */
const MIN_PROBE_INTERVAL = Milliseconds.fromSeconds(3);

/** On focus/visibility we only re-probe if the last answer is older than this. */
const FOCUS_PROBE_INTERVAL = Milliseconds.fromSeconds(30);

/** Backstop for noticing a deploy while a tab sits open and focused. */
const BUILD_POLL_INTERVAL = Milliseconds.fromMinutes(5);

let inFlight: Promise<SessionStatusPayload | null> | null = null;
let lastProbeAt = 0;
let csrfToken = '';
let buildPollTimer: ReturnType<typeof setInterval> | null = null;
let unregisterSuspectHandler: (() => void) | null = null;

export const useSessionMonitor = () => {
  const { noteServerVersions } = useBuildVersionCheck();
  const { setItem } = useLocalStorage();

  /**
   * Ask the server whether we are still logged in.
   *
   * Doubles as the CSRF mint: this is a GET through the `web` group, so Laravel
   * refreshes the XSRF-TOKEN cookie against the current session, which is what
   * makes an in-modal login possible after the old session died.
   */
  const probe = async (): Promise<SessionStatusPayload | null> => {
    if (inFlight) {
      return inFlight;
    }

    const session = useSessionStore();

    inFlight = (async () => {
      try {
        const response = await fetch(STATUS_URL, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });

        // Rate-limited. Says nothing about our session, so hold the current
        // state and let the next probe settle it — reporting "unreachable" here
        // would replace a usable password prompt with a dead end, and logged-out
        // probes share an IP bucket so a whole office can hit this at once.
        if (response.status === 429) {
          return null;
        }

        if (!response.ok) {
          // The endpoint is unauthenticated, so a non-2xx means the server is
          // unhappy rather than that we are logged out. Don't claim expiry.
          session.markUnreachable();
          return null;
        }

        const status = await response.json() as SessionStatusPayload;

        csrfToken = status.csrfToken;
        session.authProvider = status.authProvider;

        if (status.authenticated) {
          session.impersonating = Boolean(status.impersonating);
          session.markActive();
        } else {
          const alreadyExpired = session.status === 'expired';
          session.markExpired('probe');

          // Announce the transition only. Broadcasting on every probe would make
          // two open tabs bounce storage events off each other indefinitely,
          // each one's probe triggering the other's.
          if (!alreadyExpired) {
            broadcast('expired');
          }
        }

        noteServerVersions(status.build, status.contract);

        return status;
      } catch {
        // Network-level failure: offline, DNS, TLS, server down. Not an expiry.
        session.markUnreachable();
        return null;
      } finally {
        // Count failed attempts too, so a server that keeps rejecting the probe
        // can't be re-hit on every incoming 401.
        lastProbeAt = Date.now();
        inFlight = null;
      }
    })();

    return inFlight;
  };

  const probeIfStale = (maxAge: number) => {
    if (Date.now() - lastProbeAt < maxAge) {
      return;
    }

    probe();
  };

  const broadcast = (event: 'expired' | 'recovered') => {
    try {
      setItem({ key: LocalStorageKeys.session.crossTabSync, value: { event, at: Date.now() }});
    } catch {
      // Private browsing or a full quota. Cross-tab sync is a nicety, not a
      // requirement — every tab still has its own focus probe.
    }
  };

  /**
   * Called after a successful in-modal login. Puts the shell back to work
   * without reloading, so work in progress survives.
   */
  const recover = async (): Promise<void> => {
    const session = useSessionStore();
    const build = useBuildStore();
    const status = await probe();

    if (!status?.authenticated) {
      // Login reported success but we still aren't authenticated. Rather than
      // loop, hand over to the real login page.
      hardRedirectToLogin();
      return;
    }

    // A different user is now signed in — the shell is still rendered for the
    // previous identity (menu, preferences, permissions), so it can't be reused.
    if (session.bootUserId !== null && status.userId !== session.bootUserId) {
      reload();
      return;
    }

    // The probe above already folded in the server's build and contract. If that
    // declared this tab incompatible, hand over to the blocking reload modal
    // rather than pretending recovery succeeded. A merely newer build is left as
    // the dismissible prompt — they just re-authenticated, and forcing a reload
    // on top of that would throw away the work we went to this trouble to keep.
    if (build.refreshRequired) {
      return;
    }

    refreshFrames();
    broadcast('recovered');
  };

  /**
   * Restore the iframes. The active one is reloaded because it is almost
   * certainly sitting on the login page; the rest are marked expired so
   * IframeLoader unmounts them and they remount fresh (with current CSRF
   * tokens) the next time they are navigated to.
   */
  const refreshFrames = () => {
    AppStore.loadedFrames.forEach((frame) => {
      if (frame.url !== AppStore.activeFrameUrl) {
        frame.isExpired = true;
      }
    });

    AppStore.refreshCurrentPage();
  };

  const reload = () => {
    useSessionStore().suppressLeaveWarning = true;
    window.location.reload();
  };

  const hardRedirectToLogin = () => {
    useSessionStore().suppressLeaveWarning = true;
    window.location.href = '/login';
  };

  const getCsrfToken = () => csrfToken;

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      probeIfStale(FOCUS_PROBE_INTERVAL);
    }
  };

  const onFocus = () => probeIfStale(FOCUS_PROBE_INTERVAL);

  // Always probe on reconnect — this is how we leave the 'unreachable' state.
  const onOnline = () => probe();

  const onStorage = (event: StorageEvent) => {
    if (event.key !== LocalStorageKeys.session.crossTabSync || !event.newValue) {
      return;
    }

    // Another tab logged out or logged back in. Confirm for ourselves rather
    // than trusting the message, which also refreshes our CSRF token. Throttled
    // as a second guard against cross-tab probe storms.
    probeIfStale(MIN_PROBE_INTERVAL);
  };

  const register = () => {
    unregisterSuspectHandler = registerSuspectHandler(() => probeIfStale(MIN_PROBE_INTERVAL));

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    window.addEventListener('storage', onStorage);

    if (useBuildStore().enabled && buildPollTimer === null) {
      buildPollTimer = setInterval(() => {
        if (document.visibilityState === 'visible') {
          probe();
        }
      }, BUILD_POLL_INTERVAL);
    }
  };

  const unregister = () => {
    unregisterSuspectHandler?.();
    unregisterSuspectHandler = null;

    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('focus', onFocus);
    window.removeEventListener('online', onOnline);
    window.removeEventListener('storage', onStorage);

    if (buildPollTimer !== null) {
      clearInterval(buildPollTimer);
      buildPollTimer = null;
    }
  };

  return {
    getCsrfToken,
    hardRedirectToLogin,
    probe,
    recover,
    register,
    reload,
    unregister,
  };
};
